/* DIREVULF — Create Your Own font library (2026-09).

   The Text tool's font picker: search, categories and a live preview of the
   shopper's own words in every font.

   How it stays fast:
   - Only the metadata (assets/cyo-fonts.json) is read up front, and only once
     the page is idle.
   - Every font is registered as a FontFace but nothing is downloaded until a
     font is actually needed: when its row scrolls into view in the picker,
     when it is chosen, or when a saved design uses it. The browser caches
     each file after that.
   - All files are this theme's own assets. Nothing is fetched from a font
     website at runtime.

   Licensing: every entry carries font_name, font_family, font_file, category,
   license_type, commercial_use, source, author and source_url. Only fonts
   whose licence allows commercial use are offered for orders; any entry with
   commercial_use other than true is shown as unavailable and cannot be
   chosen. */
(function () {
  'use strict';

  var meta = null;          // parsed cyo-fonts.json
  var byFamily = {};        // lower-case family -> entry
  var faces = {};           // id -> { regular, bold, italic, boldItalic } FontFace
  var loading = {};         // id|variant -> Promise
  var readyPromise = null;
  var resolveReady = null;
  var ui = null;            // picker state
  var opts = {};

  function norm(s) { return String(s || '').trim().toLowerCase(); }
  function stripQuotes(f) { return String(f || '').replace(/^\s*["']|["']\s*$/g, '').trim(); }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function css(family) { return '"' + stripQuotes(family).replace(/"/g, '') + '"'; }

  /* ------------------------------------------------------------ registry */
  function entryFor(family) { return byFamily[norm(stripQuotes(family))] || null; }

  function register(f) {
    if (!window.FontFace || !document.fonts) return;
    var base = opts.base || '';
    var set = {};
    function add(key, file, desc) {
      if (!file) return;
      try {
        var face = new FontFace(f.font_family, 'url(' + base + file + ') format("woff2")', desc);
        document.fonts.add(face);
        set[key] = face;
      } catch (e) {}
    }
    var baseWeight = /-700\.woff2$/.test(f.font_file) ? '700' : '400';
    add('regular', f.font_file, { weight: baseWeight, style: 'normal', display: 'swap' });
    add('bold', f.bold_file, { weight: '700', style: 'normal', display: 'swap' });
    add('italic', f.italic_file, { weight: '400', style: 'italic', display: 'swap' });
    add('boldItalic', f.bold_italic_file, { weight: '700', style: 'italic', display: 'swap' });
    faces[f.id] = set;
  }

  function loadFace(f, key) {
    var k = f.id + '|' + key;
    if (loading[k]) return loading[k];
    var face = faces[f.id] && faces[f.id][key];
    loading[k] = face ? face.load().then(function () { return true; }, function () { return false; }) : Promise.resolve(false);
    return loading[k];
  }

  /* Loads what a layer needs: its family, in bold and/or italic when the
     font has them. Unknown families (the store's basic fonts) resolve at once. */
  function load(family, style) {
    var f = entryFor(family);
    if (!f) return Promise.resolve(false);
    style = style || {};
    var key = 'regular';
    if (style.bold && f.bold_file && style.italic && f.bold_italic_file) key = 'boldItalic';
    else if (style.bold && f.bold_file) key = 'bold';
    else if (style.italic && f.italic_file) key = 'italic';
    return key === 'regular' ? loadFace(f, 'regular') : Promise.all([loadFace(f, 'regular'), loadFace(f, key)]).then(function (r) { return r[1]; });
  }

  function supports(family, what) {
    var f = entryFor(family);
    if (!f) return true;                         // store basics: the browser decides, as before
    if (what === 'bold') return !!f.bold_file;
    if (what === 'italic') return !!f.italic_file;
    return true;
  }
  function blocked(family) {
    var f = entryFor(family);
    return !!(f && f.commercial_use !== true);
  }
  /* Production credits for the order: which font files and licences a design uses. */
  function describe(families) {
    var seen = {}, out = [];
    (families || []).forEach(function (fam) {
      var k = norm(stripQuotes(fam));
      if (!k || seen[k]) return;
      seen[k] = 1;
      var f = entryFor(fam);
      if (f) {
        out.push({
          font_name: f.font_name, font_family: f.font_family,
          font_file: (opts.base || '') + f.font_file,
          bold_file: f.bold_file ? (opts.base || '') + f.bold_file : null,
          italic_file: f.italic_file ? (opts.base || '') + f.italic_file : null,
          license_type: f.license_type, commercial_use: f.commercial_use === true,
          source: f.source, source_url: f.source_url, author: f.author
        });
      } else {
        out.push({ font_name: stripQuotes(fam), font_family: stripQuotes(fam), font_file: null, license_type: 'store basic font (not in the licensed library)', commercial_use: null, source: 'theme / device', source_url: null, author: null });
      }
    });
    return out;
  }

  /* The library file lists its fonts compactly (see row_format and
     files_bits in cyo-fonts.json); each row is expanded here into the full
     record. A font can also be listed as a full object, which is how a
     merchant adds one by hand. */
  function expand(row, data) {
    if (!Array.isArray(row)) return row;
    var id = row[0], fam = row[1], bits = row[3] | 0;
    var file = function (w) { return 'cyo-font-' + id + '-' + w + '.woff2'; };
    var f = {
      id: id, font_name: fam, font_family: fam,
      font_file: file(bits & 1 ? '400' : '700'),
      category: String(row[4] || '').split('|').filter(Boolean),
      license_type: (data.licence_codes || {})[row[2]] || row[2],
      commercial_use: true,
      source: String(data.source_template || '').replace('{id}', id),
      author: row[5] || null,
      source_url: String(data.source_url_template || '').replace('{family+}', fam.replace(/ /g, '+'))
    };
    if (bits & 2) f.bold_file = file('700');
    if (bits & 4) f.italic_file = file('400i');
    if (bits & 8) f.bold_italic_file = file('700i');
    return f;
  }

  var fetching = false;
  function fetchMeta() {
    if (fetching) return;
    fetching = true;
    if (!opts.url) { resolveReady(null); return; }
    fetch(opts.url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('fonts ' + r.status);
      return r.json();
    }).then(function (data) {
      meta = data && Array.isArray(data.fonts) ? data : null;
      if (!meta) { resolveReady(null); return; }
      meta.fonts = meta.fonts.map(function (r) { return expand(r, meta); }).filter(function (f) { return f && f.id && f.font_family && f.font_file; });
      meta.fonts.forEach(function (f) {
        if (!f || !f.font_family || !f.font_file) return;
        byFamily[norm(f.font_family)] = f;
        register(f);
      });
      resolveReady(meta);
    }).catch(function () { resolveReady(null); });
  }

  /* --------------------------------------------------------------- picker */
  function text(key, fallback) { return (opts.strings && opts.strings[key]) || fallback; }

  function sampleText() {
    var t = '';
    try { t = opts.getText ? String(opts.getText() || '') : ''; } catch (e) {}
    t = t.split('\n').map(function (s) { return s.trim(); }).filter(Boolean)[0] || '';
    if (t.length > 32) t = t.slice(0, 32) + '…';
    return t || text('fontSample', 'DIREVULF');
  }

  function allEntries() {
    var basics = (opts.basics || []).filter(Boolean).filter(function (fam) { return !entryFor(fam); }).map(function (fam, i) {
      return { id: 'basic-' + i, font_name: fam, font_family: fam, category: ['Basic'], basic: true, commercial_use: true, device: i > 0 };
    });
    return basics.concat(meta ? meta.fonts : []);
  }

  function matches(f, cat, q) {
    if (cat === 'Popular') { if (f.basic || !meta || (meta.popular || []).indexOf(f.id) === -1) return false; }
    else if (cat && cat !== 'All' && (f.category || []).indexOf(cat) === -1) return false;
    if (!q) return true;
    var hay = norm([f.font_name, f.id, (f.category || []).join(' '), f.license_type].join(' '));
    return q.split(/\s+/).every(function (w) { return hay.indexOf(w) !== -1; });
  }

  function currentFamily() { return ui && ui.select ? ui.select.value : ''; }

  function tagFor(f) {
    if (f.basic) return f.device ? text('fontDevice', 'Device font') : text('fontStore', 'Store font');
    if (f.commercial_use !== true) return text('fontPersonal', 'Personal use only');
    return f.license_type || '';
  }

  function renderList() {
    if (!ui) return;
    var q = norm(ui.search.value);
    var list = allEntries().filter(function (f) { return matches(f, ui.cat, q); });
    if (ui.cat === 'Popular' && meta) {
      var pop = meta.popular || [];
      list.sort(function (a, b) { return pop.indexOf(a.id) - pop.indexOf(b.id); });
    }
    if (ui.io) ui.io.disconnect();
    ui.list.textContent = '';
    var cur = norm(currentFamily()), sample = sampleText();
    ui.shownSample = sample;
    if (!list.length) {
      var li = el('li', 'dv-fontpick__empty', text('fontNone', 'No fonts match your search.'));
      ui.list.appendChild(li);
    }
    list.forEach(function (f) {
      var li = el('li');
      var b = el('button', 'dv-fontpick__opt');
      b.type = 'button';
      b.setAttribute('role', 'option');
      b.setAttribute('data-dv-font-pick', f.font_family);
      b.setAttribute('aria-selected', norm(f.font_family) === cur ? 'true' : 'false');
      var isBlocked = !f.basic && f.commercial_use !== true;
      if (isBlocked) b.disabled = true;
      b.appendChild(el('span', 'dv-fontpick__name', f.font_name));
      var tag = el('span', 'dv-fontpick__tag' + (isBlocked ? ' is-blocked' : ''), tagFor(f));
      b.appendChild(tag);
      var s = el('span', 'dv-fontpick__sample', sample);
      s.setAttribute('aria-hidden', 'true');
      if (f.basic) { s.style.fontFamily = css(f.font_family) + ', sans-serif'; s.classList.add('is-ready'); }
      else s.setAttribute('data-dv-font-lazy', f.id);
      b.appendChild(s);
      li.appendChild(b);
      ui.list.appendChild(li);
      if (!f.basic && ui.io) ui.io.observe(s);
    });
    var n = list.length;
    var label = text('fontCount', 'COUNT FONTS').replace('COUNT', String(n));
    if (n === 1) label = label.replace(/FONTS\b/, 'FONT').replace(/fonts\b/, 'font');
    ui.count.textContent = label;
    if (!ui.io) Array.prototype.forEach.call(ui.list.querySelectorAll('[data-dv-font-lazy]'), reveal);
  }

  /* A preview row became visible: download that one font, then draw it. */
  function reveal(s) {
    var id = s.getAttribute('data-dv-font-lazy');
    var f = meta && meta.fonts.filter(function (x) { return x.id === id; })[0];
    if (!f) return;
    s.removeAttribute('data-dv-font-lazy');
    loadFace(f, 'regular').then(function (ok) {
      s.style.fontFamily = css(f.font_family) + ', sans-serif';
      s.classList.add('is-ready');
      if (!ok) s.title = text('fontFailed', 'Preview unavailable');
    });
  }

  function renderCats() {
    var cats = ['All', 'Popular', 'Basic'].concat(meta ? meta.categories || [] : []);
    var all = allEntries();
    ui.cats.textContent = '';
    cats.forEach(function (c) {
      if (c !== 'All' && !all.some(function (f) { return matches(f, c, ''); })) return;
      var b = el('button', null, c === 'All' ? text('fontAll', 'All') : c === 'Popular' ? text('fontPopular', 'Popular') : c === 'Basic' ? text('fontBasic', 'Basic') : c);
      b.type = 'button';
      b.setAttribute('data-dv-font-cat', c);
      b.setAttribute('aria-pressed', c === ui.cat ? 'true' : 'false');
      ui.cats.appendChild(b);
    });
  }

  function setCat(c) {
    ui.cat = c;
    Array.prototype.forEach.call(ui.cats.querySelectorAll('[data-dv-font-cat]'), function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-dv-font-cat') === c ? 'true' : 'false');
    });
    renderList();
    ui.list.scrollTop = 0;
  }

  function updateCurrent() {
    if (!ui) return;
    var fam = currentFamily(), f = entryFor(fam);
    ui.curName.textContent = stripQuotes(fam) || '';
    ui.curName.style.fontFamily = fam ? css(fam) + ', sans-serif' : '';
    ui.curLic.textContent = f ? (f.license_type || '') : text('fontStore', 'Store font');
    if (f) loadFace(f, 'regular');
    var cur = norm(fam);
    Array.prototype.forEach.call(ui.list.querySelectorAll('[data-dv-font-pick]'), function (b) {
      b.setAttribute('aria-selected', norm(b.getAttribute('data-dv-font-pick')) === cur ? 'true' : 'false');
    });
  }

  function refreshSamples() {
    if (!ui) return;
    var s = sampleText();
    if (s === ui.shownSample) return;
    ui.shownSample = s;
    Array.prototype.forEach.call(ui.list.querySelectorAll('.dv-fontpick__sample'), function (n) { n.textContent = s; });
  }

  function choose(family) {
    var sel = ui.select;
    if (!sel || blocked(family)) return;
    var has = Array.prototype.some.call(sel.options, function (o) { return o.value === family; });
    if (!has) { var o = document.createElement('option'); o.value = family; o.textContent = family; sel.appendChild(o); }
    load(family, {});
    sel.value = family;
    /* The studio's own change handler applies it to the selected layer. */
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    updateCurrent();
    var layer = opts.getLayer ? opts.getLayer() : null;
    if (layer) sync(layer);
  }

  function buildPicker() {
    var host = opts.container, sel = opts.select;
    if (!host || !sel || ui) return;
    ui = { host: host, select: sel, cat: 'All', io: null, shownSample: '' };

    var cur = el('div', 'dv-fontpick__current');
    ui.curName = el('strong');
    ui.curLic = el('span', 'dv-fontpick__lic');
    cur.appendChild(ui.curName); cur.appendChild(ui.curLic);

    ui.search = el('input', 'dv-fontpick__search');
    ui.search.type = 'search';
    ui.search.setAttribute('placeholder', text('fontSearch', 'Search fonts…'));
    ui.search.setAttribute('aria-label', text('fontSearch', 'Search fonts'));
    ui.search.setAttribute('autocomplete', 'off');
    ui.search.setAttribute('enterkeyhint', 'search');

    ui.cats = el('div', 'dv-fontpick__cats');
    ui.cats.setAttribute('role', 'group');
    ui.cats.setAttribute('aria-label', text('fontCategories', 'Font categories'));
    ui.count = el('p', 'dv-fontpick__count');
    ui.count.setAttribute('aria-live', 'polite');
    ui.list = el('ul', 'dv-fontpick__list');
    ui.list.setAttribute('role', 'listbox');
    ui.list.setAttribute('aria-label', text('fontList', 'Fonts'));
    var note = el('p', 'dv-fontpick__note', text('fontNote', ''));

    host.textContent = '';
    [cur, ui.search, ui.cats, ui.count, ui.list].forEach(function (n) { host.appendChild(n); });
    if (note.textContent) host.appendChild(note);

    if ('IntersectionObserver' in window) {
      ui.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { ui.io.unobserve(e.target); reveal(e.target); }
        });
      }, { root: ui.list, rootMargin: '120px 0px' });
    }

    /* One listener per control, on the picker only. */
    ui.search.addEventListener('input', function () {
      if (ui.search.value && ui.cat !== 'All') { ui.cat = 'All'; renderCats(); }
      renderList();
      ui.list.scrollTop = 0;
    });
    ui.cats.addEventListener('click', function (e) {
      var b = e.target.closest('[data-dv-font-cat]');
      if (b) setCat(b.getAttribute('data-dv-font-cat'));
    });
    ui.list.addEventListener('click', function (e) {
      var b = e.target.closest('[data-dv-font-pick]');
      if (b && !b.disabled) choose(b.getAttribute('data-dv-font-pick'));
    });
    if (opts.textInput) opts.textInput.addEventListener('input', refreshSamples);

    /* The native select stays in the page (and in the form of the design),
       visually replaced by the picker. */
    var label = sel.closest('label');
    if (label) label.classList.add('dv-font-has-picker');
    sel.classList.add('dv-font-native');
    sel.setAttribute('tabindex', '-1');
    sel.setAttribute('aria-hidden', 'true');

    renderCats();
    renderList();
    updateCurrent();
    host.hidden = false;
  }

  function addOptions() {
    var sel = opts.select;
    if (!sel || !meta) return;
    var have = {};
    Array.prototype.forEach.call(sel.options, function (o) { have[norm(o.value)] = 1; });
    var frag = document.createDocumentFragment();
    meta.fonts.forEach(function (f) {
      if (have[norm(f.font_family)] || f.commercial_use !== true) return;
      var o = document.createElement('option');
      o.value = f.font_family; o.textContent = f.font_name;
      frag.appendChild(o);
    });
    sel.appendChild(frag);
  }

  /* Called by the studio whenever its text panel is refreshed. */
  function sync(layer) {
    if (!layer) return;
    var fam = layer.fontFamily;
    ['bold', 'italic'].forEach(function (what) {
      var btn = opts[what + 'Button'];
      if (!btn) return;
      var ok = supports(fam, what);
      btn.disabled = !ok;
      var on = ok && !!layer[what];
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (what === 'bold') btn.classList.toggle('bold-selected', on);
      if (!ok) btn.title = what === 'bold' ? text('fontNoBold', 'This font has no bold') : text('fontNoItalic', 'This font has no italic');
      else btn.removeAttribute('title');
    });
    if (ui) {
      if (ui.lastFamily !== fam) { ui.lastFamily = fam; updateCurrent(); }
      refreshSamples();
    }
  }

  function init(o) {
    if (readyPromise) return readyPromise;
    opts = o || {};
    readyPromise = new Promise(function (res) { resolveReady = res; });
    var go = function () { fetchMeta(); };
    if (opts.eager) go();
    else if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 2500 });
    else setTimeout(go, 800);
    readyPromise.then(function (m) {
      if (!m) return;        // library unavailable: the original select keeps working
      addOptions();
      buildPicker();
    });
    return readyPromise;
  }

  window.DVFonts = {
    version: '1.0.0',
    init: init,
    ready: function () { return readyPromise || Promise.resolve(null); },
    /* Starts the metadata request now (used when a design needs a font). */
    now: function () { if (readyPromise) fetchMeta(); return this.ready(); },
    find: entryFor,
    supports: supports,
    blocked: blocked,
    load: load,
    describe: describe,
    sync: sync,
    css: css,
    count: function () { return meta ? meta.fonts.length : 0; }
  };
})();